/**
 * UnifiedAIChatButton - Floating action button to open unified AI chat
 * Context-aware based on current page/route
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import UnifiedAIChat, { type UnifiedAIChatContext } from './UnifiedAIChat';

interface UnifiedAIChatButtonProps {
  // Optional: override auto-detected context
  context?: UnifiedAIChatContext;
  onTaskClick?: (taskId: string, boardId: string) => void;
  onBoardClick?: (boardId: string) => void;
  onProjectClick?: (projectId: string) => void;
  onMeetingClick?: (meetingId: string) => void;
  /** Position variant - 'floating' for FAB, 'toolbar' for inline button */
  variant?: 'floating' | 'toolbar';
}

export default function UnifiedAIChatButton({
  context: overrideContext,
  onTaskClick,
  onBoardClick,
  onProjectClick,
  onMeetingClick,
  variant = 'floating',
}: UnifiedAIChatButtonProps) {
  const { t } = useTranslation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const location = useLocation();

  // Auto-detect context from URL if not provided
  const getContextFromRoute = (): UnifiedAIChatContext => {
    if (overrideContext) return overrideContext;

    const path = location.pathname;

    // Board context: /boards/:boardId or /projects/:projectId/boards/:boardId
    const boardMatch = path.match(/\/boards\/([a-f0-9-]+)/);
    if (boardMatch?.[1]) {
      return {
        type: 'board',
        boardId: boardMatch[1],
        contextLabel: 'Board Context',
      };
    }

    // Project context: /projects/:projectId
    const projectMatch = path.match(/\/projects\/([a-f0-9-]+)/);
    if (projectMatch?.[1] && !boardMatch) {
      return {
        type: 'project',
        projectId: projectMatch[1],
        contextLabel: 'Project Context',
      };
    }

    // Meeting context: /meetings/:meetingId
    const meetingMatch = path.match(/\/meetings\/([a-f0-9-]+)/);
    if (meetingMatch?.[1]) {
      return {
        type: 'meeting',
        meetingId: meetingMatch[1],
        contextLabel: 'Meeting Context',
      };
    }

    // Global context (dashboard, tasks list, etc.)
    return {
      type: 'global',
      contextLabel: 'Global',
    };
  };

  const context = getContextFromRoute();

  if (variant === 'toolbar') {
    return (
      <>
        <button
          onClick={() => setIsChatOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700 text-sm font-medium shadow-sm transition-all"
          title={t('chat.aiAssistant')}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className="hidden sm:inline">{t('chat.aiAssistant')}</span>
        </button>

        {isChatOpen && (
          <UnifiedAIChat
            context={context}
            onClose={() => setIsChatOpen(false)}
            onTaskClick={onTaskClick}
            onBoardClick={onBoardClick}
            onProjectClick={onProjectClick}
            onMeetingClick={onMeetingClick}
          />
        )}
      </>
    );
  }

  // Floating variant (FAB)
  return (
    <>
      {!isChatOpen && (
        <button
          onClick={() => setIsChatOpen(true)}
          className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-40 flex items-center justify-center w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 hover:scale-105 transition-all"
          title={t('chat.aiAssistant')}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </button>
      )}

      {isChatOpen && (
        <UnifiedAIChat
          context={context}
          onClose={() => setIsChatOpen(false)}
          onTaskClick={onTaskClick}
          onBoardClick={onBoardClick}
          onProjectClick={onProjectClick}
          onMeetingClick={onMeetingClick}
        />
      )}
    </>
  );
}
